'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';
import {
  MapPin,
  UserRound,
  LayoutGrid,
  CalendarCheck,
  Instagram,
  MessageCircle,
  Mail,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import type { LandingPageProps } from '../_lib/landing-page.types';

import './styles.css';

function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-[var(--ryze-bg)] min-h-[720px] lg:min-h-[820px] flex items-center">
      {/* Hero background image */}
      <Image
        src="/landing-pages/ryze/images/hero.png"
        alt="Ryze tennis court"
        fill
        priority
        sizes="100vw"
        className="object-cover"
      />
      {/* Dark gradient overlay for legibility and bottom fade into next section */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[var(--ryze-bg)]"
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-black/15"
      />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-8 w-full flex flex-col items-center text-center pt-24 pb-24">
        <h1 className="ryze-animate-fade-up ryze-delay-1 text-white font-ryze-display uppercase text-4xl sm:text-5xl lg:text-[40px] leading-none mb-5">
          Book Now!
        </h1>

        <div className="ryze-animate-fade-up ryze-delay-2 inline-block bg-[var(--ryze-lime)] px-6 py-1 mb-8">
          <span className="text-[var(--ryze-bg)] font-ryze-display uppercase text-4xl sm:text-5xl lg:text-[50px] leading-none">
            Own The Game.
          </span>
        </div>

        <div className="ryze-animate-fade-up ryze-delay-3 flex flex-col sm:flex-row gap-4">
          <Link
            href="/book"
            className="inline-flex items-center justify-center bg-[var(--ryze-lime)] text-[var(--ryze-bg)] font-semibold text-sm tracking-wide uppercase h-11 px-6 rounded-[4px] hover:bg-[var(--ryze-lime-bright)] transition-colors duration-200"
          >
            Book Now!
          </Link>
          <Link
            href="#about"
            className="inline-flex items-center justify-center border border-[var(--ryze-blue)] text-white font-semibold text-sm tracking-wide uppercase h-11 px-6 rounded-[5px] hover:bg-white/5 transition-colors duration-200"
          >
            Learn More
          </Link>
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  const features = [
    { icon: MapPin, label: 'Prime Location' },
    { icon: UserRound, label: 'Professional Coach' },
    { icon: LayoutGrid, label: 'Excellent Surfaces' },
    { icon: CalendarCheck, label: 'Instant Booking' },
  ];

  return (
    <section id="about" className="bg-[var(--ryze-bg)] py-20 lg:py-24">
      <div className="max-w-[1140px] mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="text-white font-ryze-display text-3xl lg:text-4xl mb-6">
            About Us
          </h2>
          <p className="text-white/70 text-base leading-relaxed">
            Founded in Jeddah in 2026, Ryze is a tennis club and academy that
            combines a professional facility and qualified coaches. We are
            located in SAUDIA CITY SPORTS CLUB and our facility consists of 3
            tennis courts.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="ryze-feature-card bg-[var(--ryze-surface)]/60 border border-white/10 rounded-xl p-6 flex flex-col gap-4"
            >
              <div className="w-10 h-10 rounded-lg bg-[var(--ryze-lime)]/15 flex items-center justify-center text-[var(--ryze-lime)]">
                <Icon className="w-5 h-5" />
              </div>
              <span className="text-white font-semibold text-sm">
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LazyMap({ src, title }: { src: string; title: string }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={mapRef}
      className="rounded-2xl overflow-hidden border border-white/10 h-full min-h-[360px] bg-[var(--ryze-surface)]"
    >
      {isLoaded ? (
        <iframe
          src={src}
          title={title}
          className="w-full h-full border-0"
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-white/30">
          <MapPin className="w-8 h-8" />
        </div>
      )}
    </div>
  );
}

function getEmbedUrl(
  googleMapsUrl: string,
  address?: string | null,
  name?: string | null,
): string {
  try {
    if (googleMapsUrl.includes('/embed') || googleMapsUrl.includes('output=embed')) {
      return googleMapsUrl;
    }
    if (name && address) {
      return `https://www.google.com/maps?q=${encodeURIComponent(`${name} ${address}`)}&output=embed`;
    }
    if (address) {
      return `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
    }
    const placeMatch = googleMapsUrl.match(/\/place\/([^/?]+)/);
    if (placeMatch?.[1]) {
      const query = decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
      return `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
    }
    const coordMatch = googleMapsUrl.match(/@(-?[\d.]+),(-?[\d.]+)/);
    if (coordMatch?.[1] && coordMatch?.[2]) {
      return `https://www.google.com/maps?q=${coordMatch[1]},${coordMatch[2]}&output=embed`;
    }
    return `https://www.google.com/maps?q=${encodeURIComponent(googleMapsUrl)}&output=embed`;
  } catch {
    return googleMapsUrl;
  }
}

const FALLBACK_LOCATION = {
  name: 'Ryze Tennis',
  address: 'Saudia City Sports Club, Jeddah, Alkhaldiya District',
  city: 'Jeddah',
  google_maps_url: 'https://www.google.com/maps?q=Saudia+City+Sports+Club+Jeddah',
  google_maps_embed_url:
    'https://www.google.com/maps?q=Saudia+City+Sports+Club+Jeddah&output=embed',
};

function ContactSection({ accountId }: { accountId?: string }) {
  const supabase = useSupabase();
  const { data: locations } = useQuery({
    queryKey: ['ryze-locations', accountId],
    queryFn: async () => {
      if (!accountId) return [];

      const { data, error } = await supabase.rpc('get_public_locations', {
        p_account_id: accountId,
      });

      if (error || !data) return [];
      return data;
    },
    enabled: !!accountId,
  });

  const location = locations?.[0] ?? FALLBACK_LOCATION;
  const mapSrc =
    location.google_maps_embed_url ||
    getEmbedUrl(
      location.google_maps_url ?? '',
      location.address,
      location.name,
    );

  return (
    <section id="location" className="bg-[var(--ryze-bg-deep)] py-16 lg:py-20">
      <div className="max-w-[1140px] mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-stretch">
          {/* Left: Contact details */}
          <div className="flex flex-col">
            <h2 className="text-[var(--ryze-lime)] font-ryze-display text-3xl lg:text-4xl mb-8">
              Contact Information
            </h2>

            <div className="mb-8">
              <h3 className="text-white font-semibold text-sm mb-3">Address</h3>
              <p className="text-white/70 leading-relaxed max-w-md">
                {location.address}
                {location.city ? `, ${location.city}` : ''}
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-white font-semibold text-sm mb-3">
                Social Links
              </h3>
              <div className="flex items-center gap-3">
                <a
                  href="https://wa.me/966553165262"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                  className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/80 hover:text-[var(--ryze-lime)] hover:border-[var(--ryze-lime)]/40 transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                </a>
                <a
                  href="https://www.instagram.com/ryzetennis"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/80 hover:text-[var(--ryze-lime)] hover:border-[var(--ryze-lime)]/40 transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-white font-semibold text-sm mb-3">Email</h3>
              <a
                href="mailto:info@ryzetennis.sa"
                className="inline-flex items-center gap-2 text-[var(--ryze-lime)] hover:opacity-80 transition-opacity"
              >
                <Mail className="w-4 h-4" />
                info@ryzetennis.sa
              </a>
            </div>
          </div>

          {/* Right: Map */}
          <LazyMap src={mapSrc} title={`${location.name} map`} />
        </div>
      </div>
    </section>
  );
}

export default function RyzeLandingPage({ tenant }: LandingPageProps) {
  return (
    <div className="min-h-screen bg-[var(--ryze-bg)]">
      <HeroSection />
      <AboutSection />
      <ContactSection accountId={tenant?.id} />
    </div>
  );
}
